import { StrictMode, useSyncExternalStore } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/query-client";
import { router } from "./router";
import { useAuth } from "./lib/auth";
import { useCustomerStore } from "./store";
import { setupSessionSync } from "./lib/session-sync";
import { seedResolvedTheme } from "./hooks/use-theme-sync";
import "@fontsource/ibm-plex-sans/latin-400.css";
import "@fontsource/ibm-plex-sans/latin-500.css";
import "@fontsource/ibm-plex-sans/latin-600.css";
import "./styles/app.css";

// Sibling-tab session convergence: adopt a sibling's token rotation into this
// tab's store, and propagate a sibling's sign-out through the full teardown.
setupSessionSync();

// The OS theme reaches the store before the first render, so the first brand
// the signed-in layout applies is already the resolved theme's.
seedResolvedTheme();

const { onFinishHydration, hasHydrated } = useCustomerStore.persist;

function useHasHydrated(): boolean {
  return useSyncExternalStore(onFinishHydration, hasHydrated, () => false);
}

function App() {
  const hydrated = useHasHydrated();
  const auth = useAuth();

  if (!hydrated) return null;

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} context={{ auth }} />
    </QueryClientProvider>
  );
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
